/**
 * Marketing landing — public, anonymous-visitor home at `/`.
 *
 * Structure mirrors the production landing at afyanex.com. Heavy sections
 * extracted into ./components/marketing/landing/* so this file remains an
 * orchestration shell.
 *
 *   <LandingHeader />
 *   <LandingHero />              // hero + 3-stat strip
 *   <Features grid + compare card>
 *   <Admin console preview>
 *   <Three roles>
 *   <How it works>
 *   <Schedule preview>
 *   <Caregiver mobile preview>
 *   <Family portal preview>
 *   <LandingPricing />
 *   <LandingTrustAndFAQ />
 *   <LandingClosing />           // 2-ways-to-start + footer
 *
 * Per English-only mandate: no language picker / multilingual claim anywhere.
 */
import { useEffect } from "react";
import {
  Mic, Sparkles, Calendar, Megaphone, FileText, WifiOff, ShieldCheck,
  Check, Building2, UserRound, Users,
} from "lucide-react";
import AnimatedHeroDemo from "@/components/marketing/AnimatedHeroDemo"; // eslint-disable-line no-unused-vars
import CaregiverPhoneMock from "@/components/marketing/CaregiverPhoneMock";
import { SectionHeading, Eyebrow, track } from "@/components/marketing/landing/_shared";
import LandingHeader from "@/components/marketing/landing/Header";
import LandingHero from "@/components/marketing/landing/Hero";
import LandingPricing from "@/components/marketing/landing/Pricing";
import LandingTrustAndFAQ from "@/components/marketing/landing/TrustAndFAQ";
import LandingClosing from "@/components/marketing/landing/Closing";

const FEATURES = [
  { icon: Mic, title: "Voice → AI clinical note",
    body: "Caregiver speaks; Afyanex transcribes and formats it into an audit-ready note in seconds." },
  { icon: Calendar, title: "Drag-and-drop scheduling",
    body: "Build the week visually. Skill conflicts flagged before you confirm." },
  { icon: Megaphone, title: "Open-shift broadcast",
    body: "Caregiver calls off? One tap broadcasts to qualified aides. No 6 AM phone tree." },
  { icon: FileText, title: "One-click audit PDFs",
    body: "Any date range, any client, any caregiver. Three days of audit prep becomes one export." },
  { icon: WifiOff, title: "Works offline",
    body: "Drafts save locally with no signal. Auto-sync the moment connectivity returns." },
  { icon: ShieldCheck, title: "BAA on every plan",
    body: "HIPAA-aligned, encrypted, audit-logged. BAA signed in-app before any PHI." },
];

const ROLES = [
  { icon: Building2, title: "Agency admin",
    body: "Live shift status, missed-visit alerts, expiring certifications, payroll exports, invoices — one console." },
  { icon: UserRound, title: "Caregiver · Aide · PSW",
    body: "Clock in, speak naturally, submit. Two-minute shift notes that get out of the way." },
  { icon: Users, title: "Family member",
    body: "A link. No login. Branded summaries of the last 14 days. Stops the office calls." },
];

const STEPS = [
  { n: "01", title: "Dictate or jot",
    body: "Caregiver finishes care, hits the mic or types rough notes — we transcribe in seconds." },
  { n: "02", title: "AI formats",
    body: "Our purpose-built AI rewrites the raw text into structured, clinical-grade documentation." },
  { n: "03", title: "Review & edit",
    body: "Preview shows the final note alongside the raw input and checklist. Edit, re-format." },
  { n: "04", title: "Submit & sync",
    body: "Auto clock-out, audit log written, family portal updated, PDFs ready in the admin console." },
];

export default function MarketingLanding() {
  // Funnel — fires once when an anonymous (or any) visitor lands on /.
  useEffect(() => {
    track("landing_viewed", { path: "/" });
  }, []);

  return (
    <div className="min-h-screen bg-clinical-bg" data-testid="marketing-landing">
      <LandingHeader />
      <LandingHero />

      {/* Features grid */}
      <section id="features" className="relative mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <SectionHeading
          eyebrow="Everything you need"
          sub="Scheduling, documentation, EVV, compliance, and family communication — without a six-month implementation."
        >
          One platform. <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">Three tools replaced.</span>
        </SectionHeading>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {FEATURES.map((f, i) => (
            <div key={f.title}
                 data-testid={`landing-feature-${i}`}
                 className="group rounded-2xl bg-white border border-clinical-border/70 p-6 transition-all hover:-translate-y-1 hover:shadow-[0_20px_50px_-25px_rgba(11,21,48,0.18)] hover:border-afyanex-blue/30">
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-afyanex-blue/10 text-afyanex-blue group-hover:bg-afyanex-blue group-hover:text-white transition-colors">
                <f.icon className="h-4.5 w-4.5" strokeWidth={2.2} />
              </span>
              <h3 className="mt-5 font-heading font-semibold text-[17px] text-clinical-text">{f.title}</h3>
              <p className="mt-2 text-[14px] text-clinical-subtext leading-relaxed">{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Voice-to-record compare */}
      <section className="bg-white border-y border-clinical-border/60">
        <div className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
          <SectionHeading
            eyebrow="The magic, on one page"
            sub="A 45-minute documentation chore becomes a 2-minute drive home — without losing a clinical detail."
          >
            Caregivers speak. <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">Afyanex writes the chart.</span>
          </SectionHeading>
          <div className="grid md:grid-cols-2 gap-5 max-w-5xl mx-auto">
            <div className="rounded-2xl bg-clinical-bg border border-clinical-border/70 p-7">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-clinical-subtext">What the caregiver says</div>
              <div className="mt-2 flex items-center gap-2 text-xs text-clinical-subtext">
                <Mic className="h-3.5 w-3.5 text-afyanex-pink" /> 0:42 · drive home
              </div>
              <p className="mt-5 text-[15.5px] leading-[1.65] text-clinical-text italic">
                "Ate breakfast around 8, walked the hallway with the walker, said
                her left knee was a bit sore after. Did her BP — 128 over 80. Gave
                her metformin with breakfast, she took it no problem."
              </p>
            </div>
            <div className="rounded-2xl bg-white border border-afyanex-blue/15 p-7 shadow-[0_20px_60px_-30px_rgba(0,94,255,0.3)]">
              <div className="flex items-center justify-between">
                <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-afyanex-blue">What the record shows</div>
                <span className="inline-flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                  <Sparkles className="h-3 w-3" /> Generated in 1.4s
                </span>
              </div>
              <p className="mt-5 text-[15px] leading-[1.65] text-clinical-text">
                Client tolerated breakfast at approximately 08:00 with adequate
                oral intake. Ambulated independently with walker assistance in the
                hallway; reported mild left knee discomfort post-ambulation — no
                signs of acute distress observed.
              </p>
              <p className="mt-3 text-[15px] leading-[1.65] text-clinical-text">
                Vital signs: BP 128/80 mmHg. Metformin administered as scheduled;
                medication accepted without difficulty.
              </p>
              <div className="mt-5 flex flex-wrap gap-1.5">
                {["Meds administered", "Vitals captured", "Ambulation", "Mild knee pain noted"].map((t) => (
                  <span key={t} className="text-[11px] rounded-full bg-afyanex-blue/10 text-afyanex-blue px-2.5 py-1 font-medium">{t}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Admin console preview */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <SectionHeading
          eyebrow="One console, one source of truth"
          sub="Live shift status, missed-visit alerts, compliance expiry, and risk-assessment reviews — surfaced the moment they need your attention. No more juggling spreadsheets, paper rosters, and three browser tabs."
        >
          Everything an agency owner needs <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">in one screen.</span>
        </SectionHeading>
        <div className="relative" data-testid="landing-admin-preview">
          <div className="rounded-2xl bg-white border border-clinical-border/70 shadow-[0_40px_100px_-40px_rgba(11,21,48,0.35)] overflow-hidden">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-clinical-border/60 bg-clinical-bg/60">
              <div className="flex gap-1.5">
                <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
                <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
                <span className="h-3 w-3 rounded-full bg-[#28C840]" />
              </div>
              <div className="flex-1 text-center text-[11px] text-clinical-subtext font-medium tracking-wide">
                app.afyanex.com/admin
              </div>
              <div className="w-12" />
            </div>
            <img src="/marketing/admin-dashboard.jpg" alt="Afyanex admin dashboard — Today at a glance"
                 className="w-full h-auto block" loading="lazy" />
          </div>
          <div className="hidden md:flex absolute -left-4 top-1/3 items-center gap-3 rounded-xl bg-white border border-clinical-border/70 px-4 py-3 shadow-[0_18px_40px_-15px_rgba(11,21,48,0.18)]">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div className="text-[12px]">
              <div className="font-semibold text-clinical-text">Compliance ready</div>
              <div className="text-clinical-subtext">26 caregivers · audit-grade</div>
            </div>
          </div>
          <div className="hidden md:flex absolute -right-4 top-2/3 items-center gap-3 rounded-xl bg-white border border-clinical-border/70 px-4 py-3 shadow-[0_18px_40px_-15px_rgba(11,21,48,0.18)]">
            <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-afyanex-pink/10 text-afyanex-pink">
              <Sparkles className="h-4 w-4" />
            </span>
            <div className="text-[12px]">
              <div className="font-semibold text-clinical-text">Voice ROI · this week</div>
              <div className="text-clinical-subtext">15 min saved · 21 notes</div>
            </div>
          </div>
        </div>
      </section>

      {/* Three roles */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <SectionHeading eyebrow="Three roles, one platform">
          Built for everyone in a <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">home care visit.</span>
        </SectionHeading>
        <div className="grid md:grid-cols-3 gap-5">
          {ROLES.map((r) => (
            <div key={r.title}
                 className="rounded-2xl bg-white border border-clinical-border/70 p-7 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(11,21,48,0.18)] transition-all">
              <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-afyanex-blue/10 text-afyanex-blue">
                <r.icon className="h-5 w-5" strokeWidth={2} />
              </span>
              <h3 className="mt-5 font-heading text-xl font-semibold text-clinical-text">{r.title}</h3>
              <p className="mt-2 text-[14.5px] text-clinical-subtext leading-relaxed">{r.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="bg-white border-y border-clinical-border/60">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-24">
          <SectionHeading
            eyebrow="How it works"
            sub="A complete shift takes minutes — not the 20 to 40 caregivers normally spend re-writing notes after the visit."
          >
            From bedside to audit log in <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">four steps.</span>
          </SectionHeading>
          <div className="space-y-3">
            {STEPS.map((s, i) => (
              <div key={s.n}
                   data-testid={`landing-step-${i}`}
                   className="grid grid-cols-[auto_1fr] sm:grid-cols-[120px_1fr] gap-5 sm:gap-8 items-start py-6 border-b border-clinical-border/60 last:border-0">
                <div className="font-heading text-3xl sm:text-5xl font-semibold tracking-tight text-afyanex-blue/30">
                  {s.n}
                </div>
                <div>
                  <h3 className="font-heading text-xl sm:text-2xl font-semibold text-clinical-text">{s.title}</h3>
                  <p className="mt-2 text-[15px] text-clinical-subtext leading-relaxed max-w-2xl">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Schedule preview */}
      <section className="mx-auto max-w-6xl px-5 sm:px-8 py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <Eyebrow>Drag, drop, done</Eyebrow>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-clinical-text leading-[1.1]">
              The week, <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">built visually.</span>
            </h2>
            <p className="mt-5 text-base sm:text-lg text-clinical-subtext leading-relaxed">
              A weekly grid of caregivers × days. Drop a visit into any cell —
              we flag skill, language, and availability conflicts the second
              they happen, before the client ever notices.
            </p>
            <ul className="mt-6 space-y-3">
              {[
                "Voice-create visits in seconds ('Sarah, Margaret, Tuesday 9 to 11').",
                "Open-shift broadcast emails qualified aides when someone calls off.",
                "Recurring patterns, time-off, and skill conflicts handled automatically.",
              ].map((t) => (
                <li key={t} className="flex items-start gap-2.5 text-[15px]">
                  <Check className="h-4 w-4 text-afyanex-blue mt-1 shrink-0" />
                  <span className="text-clinical-text leading-relaxed">{t}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="relative" data-testid="landing-schedule-preview">
            <div className="rounded-2xl bg-white border border-clinical-border/70 shadow-[0_40px_100px_-40px_rgba(0,94,255,0.35)] overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-clinical-border/60 bg-clinical-bg/60">
                <div className="flex gap-1.5">
                  <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
                  <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
                  <span className="h-3 w-3 rounded-full bg-[#28C840]" />
                </div>
                <div className="flex-1 text-center text-[11px] text-clinical-subtext font-medium tracking-wide">
                  app.afyanex.com/admin/schedule
                </div>
                <div className="w-12" />
              </div>
              <img src="/marketing/admin-schedule.jpg" alt="Afyanex weekly schedule — caregivers × days grid"
                   className="w-full h-auto block" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* Caregiver mobile */}
      <section className="relative overflow-hidden bg-white border-y border-clinical-border/60">
        <div aria-hidden className="pointer-events-none absolute top-1/3 right-0 h-[300px] w-[300px] rounded-full bg-afyanex-pink/10 blur-[100px]" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-24">
          <div className="grid lg:grid-cols-[1fr_auto] gap-16 items-center">
            <div>
              <Eyebrow>The caregiver, on the doorstep</Eyebrow>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-clinical-text leading-[1.1]">
                Two-minute shift notes — <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">on the way to the car.</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-clinical-subtext leading-relaxed max-w-xl">
                One screen, designed for a phone held with one hand. Caregivers
                see who they're with, what to dictate, which meds to log, and
                what the next caregiver needs to know — without ever opening a
                second app.
              </p>
              <ul className="mt-7 space-y-3 max-w-lg">
                {[
                  { t: "AI handoff briefing on arrival", d: "Knows what happened last shift before they walk in." },
                  { t: "One-tap voice fill", d: "Vitals, mood, fluid, activity, notes — fills in seconds." },
                  { t: "Tap-to-log MAR", d: "Given / Refused / Held on every medication, audit-tied." },
                  { t: "Family update without typing", d: "AI writes a warm message families actually want to read." },
                ].map((row) => (
                  <li key={row.t} className="flex items-start gap-3">
                    <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-md bg-afyanex-blue/10 text-afyanex-blue shrink-0">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <div>
                      <div className="font-medium text-clinical-text text-[15px]">{row.t}</div>
                      <div className="text-[13.5px] text-clinical-subtext leading-relaxed">{row.d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="justify-self-center lg:justify-self-end">
              <CaregiverPhoneMock />
            </div>
          </div>
        </div>
      </section>

      {/* Family portal preview */}
      <section className="relative overflow-hidden bg-clinical-bg">
        <div aria-hidden className="pointer-events-none absolute -top-10 left-0 h-[320px] w-[320px] rounded-full bg-afyanex-cyan/15 blur-[120px]" />
        <div className="relative mx-auto max-w-6xl px-5 sm:px-8 py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Eyebrow>The family, in the loop</Eyebrow>
              <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-clinical-text leading-[1.1]">
                Anxious phone calls, <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">replaced with calm updates.</span>
              </h2>
              <p className="mt-5 text-base sm:text-lg text-clinical-subtext leading-relaxed max-w-xl">
                Send the family a private link. No app to install, no password
                to manage — just warm, AI-polished summaries from the last 14
                days, branded as your agency. Trust without effort.
              </p>
              <ul className="mt-7 space-y-3 max-w-lg">
                {[
                  { t: "AI-warmed shift summaries", d: "We rewrite the clinical note into something a daughter actually wants to read." },
                  { t: "Caregiver verified badge", d: "Background checks, insurance, certifications — visible to the family." },
                  { t: "Revocable + time-limited", d: "Set expiry per invite (7 / 30 / 90 / 180 days). Revoke any time, one click." },
                  { t: "Branded to your agency", d: "Your logo, your colours. Families see your agency — not 'Afyanex'." },
                ].map((row) => (
                  <li key={row.t} className="flex items-start gap-3">
                    <span className="mt-1 flex h-5 w-5 items-center justify-center rounded-md bg-afyanex-pink/10 text-afyanex-pink shrink-0">
                      <Check className="h-3 w-3" strokeWidth={3} />
                    </span>
                    <div>
                      <div className="font-medium text-clinical-text text-[15px]">{row.t}</div>
                      <div className="text-[13.5px] text-clinical-subtext leading-relaxed">{row.d}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative" data-testid="landing-family-mock">
              <div className="rounded-2xl bg-white border border-clinical-border/70 shadow-[0_40px_100px_-40px_rgba(255,70,163,0.4)] overflow-hidden">
                <div className="flex items-center gap-3 px-4 py-3 border-b border-clinical-border/60 bg-clinical-bg/60">
                  <div className="flex gap-1.5">
                    <span className="h-3 w-3 rounded-full bg-[#FF5F57]" />
                    <span className="h-3 w-3 rounded-full bg-[#FEBC2E]" />
                    <span className="h-3 w-3 rounded-full bg-[#28C840]" />
                  </div>
                  <div className="flex-1 text-center text-[11px] text-clinical-subtext font-medium tracking-wide">
                    afyanex.com/family/···
                  </div>
                  <div className="w-12" />
                </div>
                <img src="/marketing/family-portal.jpg" alt="Afyanex family portal — care updates for Margaret"
                     className="w-full h-auto block" loading="lazy" />
              </div>
              <div className="hidden md:flex absolute -left-5 top-1/3 items-center gap-3 rounded-xl bg-white border border-clinical-border/70 px-4 py-3 shadow-[0_18px_40px_-15px_rgba(11,21,48,0.18)]">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-afyanex-pink/10 text-afyanex-pink">
                  <Users className="h-4 w-4" />
                </span>
                <div className="text-[12px]">
                  <div className="font-semibold text-clinical-text">No login</div>
                  <div className="text-clinical-subtext">Just a private link</div>
                </div>
              </div>
              <div className="hidden md:flex absolute -right-4 bottom-8 items-center gap-3 rounded-xl bg-white border border-clinical-border/70 px-4 py-3 shadow-[0_18px_40px_-15px_rgba(11,21,48,0.18)]">
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-100 text-emerald-600">
                  <ShieldCheck className="h-4 w-4" />
                </span>
                <div className="text-[12px]">
                  <div className="font-semibold text-clinical-text">Caregiver verified</div>
                  <div className="text-clinical-subtext">Background · Insurance</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LandingPricing />
      <LandingTrustAndFAQ />
      <LandingClosing />
    </div>
  );
}
